import { PaginatedResponse } from '@libs/models/common';
import { EmploymentType, StaffingStatus, StaffListItem } from '@libs/models/talents';
import { filterToQueryParams, transformSorting } from '../utils';
import { request } from '../request';
import dayjs from 'dayjs';
import { FiltersCollection, SortingModel } from '@libs/store/filters';

export type GetTalentsParams = {
  orderBy?: SortingModel;
  search?: string;
  page?: number;
  size?: number;
  filters?: FiltersCollection;
};
export async function getTalents(params?: GetTalentsParams): Promise<PaginatedResponse<StaffListItem[]>> {
  const filters = filterToQueryParams(params?.filters, {
    renameMap: {
      specialties: 'specialities',
      status: 'staffingStatuses',
      qualifications: 'nurseQualifications',
      locations: 'locations',
      search: 'search',
    },
  });
  const sorting = transformSorting(params?.orderBy, {
    renameMap: {
      name: 'fullName',
      email: 'email',
      phone: 'phoneNumber',
      status: 'staffingStatus',
      lastWorkedTime: 'lastWorkedTime',
      qualifications: 'nursequalification_abbreviation',
      specialties: 'specialty_name',
    },
  });
  const response = await request.get<PaginatedResponse<StaffListItemResponse[]>>('/v2/staff', {
    params: {
      pageNumber: params?.page,
      pageSize: params?.size,
      orderBy_in: sorting,
      search: params?.search,
      ...filters,
    },
  });
  return {
    items: response.data.items.map((entity) => ({
      ...entity,
      staffingType: 'Agency',
      address: entity.firstAddress,
      fullName: [entity?.firstName || '', entity?.lastName || ''].filter(Boolean).join(' '),
      lastWorkedTime: entity.lastWorkedTime ? dayjs(entity.lastWorkedTime) : dayjs(),
      employmentType: entity.employmentType as EmploymentType,
      status: entity.staffingStatus as StaffingStatus,
      specialties: entity.specialities,
      qualifications: entity.nurseQualifications,
      credentials: entity.credentials
        ? entity.credentials.map((c) => ({
            ...c,
            type: c.type,
            name: c.fileName,
            url: c.filePath,
          }))
        : [],
    })),
    pageNumber: response.data.pageNumber,
    pageSize: response.data.pageSize,
    totalCount: response.data.totalCount,
  };
}

type StaffListItemResponse = {
  id: string; // 'staffmember-01k36pnjb3d5xrxnm9dcbjxvr6';
  imageUrl: string; // 'https://www.samplestaff.com/staff/id';
  firstName: string; // 'FirstName';
  lastName: string; // 'LastName';
  phoneNumber: string; // '+1 (123) 456-7890';
  email: string; // 'sample@gmail.com';
  firstAddress: {
    firstAddress: string; // 'FirstAddress';
    city: string; // 'City';
    state: string; // 'State';
    zipCode: string; // '11111';
    secondAddress: string; // 'SecondAddress';
  };
  employmentType: 'HourlyPartTime';
  staffingStatus: 'Active' | 'Deactivated';
  lastWorkedTime: string; // '2025-08-21T16:16:24.4193998Z';
  nurseQualifications: [
    {
      id: string; // 'nursequalification-01k36pnjb3rrg6e48qmeqwes6h';
      name: string; // 'Name';
      abbreviation: string; // 'Abbreviation';
      category: string; // 'Category';
    },
  ];
  specialities: [
    {
      id: string; // 'speciality-01k36pnjb36vprtj4mnh4ne0zr';
      name: string; // 'Name';
      category: string; // 'Category';
    },
  ];
  credentials: {
    type: 'License' | 'Certification' | 'BackgroundCheck' | 'HealthRecord' | 'Assessment' | 'EDocument';
    fileName: string; //  'File Name.pdf'; - any human-readable name (not the physical file name)
    filePath: string; //  'https://example.com/file.pdf';
  }[];
};
